import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { galleryImages } from '../../mock';

const IMAGES_PER_PAGE = 9;

const getTouchDist = (touches) =>
  Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY);

// Seeded random shuffle to ensure consistent order per session but different across page loads
const shuffleArray = (array) => {
  const shuffled = [...array];
  // Use current timestamp seed for randomization on each page load
  let seed = Date.now() % 1000;
  for (let i = shuffled.length - 1; i > 0; i--) {
    seed = (seed * 9301 + 49297) % 233280;
    const j = Math.floor((seed / 233280) * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const Gallery = ({ t, language }) => {
  const [galleryPage, setGalleryPage] = useState(0);
  const [selectedImage, setSelectedImage] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  
  // Touch handling refs
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const galleryTouchStartX = useRef(0);
  const galleryTouchEndX = useRef(0);

  // Pinch-to-zoom state for lightbox
  const [zoom, setZoom] = useState({ scale: 1, x: 0, y: 0 });
  const pinchRef = useRef({ dist: 0, scale: 1, pinching: false });
  const panRef = useRef({ startX: 0, startY: 0, panning: false });
  const lastTapRef = useRef(0);

  const resetZoom = useCallback(() => {
    pinchRef.current.pinching = false;
    panRef.current.panning = false;
    setZoom({ scale: 1, x: 0, y: 0 });
  }, []);

  // Randomize gallery images once on mount
  const randomizedImages = useMemo(() => shuffleArray(galleryImages), []);

  // Memoize pagination values
  const totalGalleryPages = useMemo(() => Math.ceil(randomizedImages.length / IMAGES_PER_PAGE), [randomizedImages.length]);
  
  const currentGalleryImages = useMemo(() => {
    const start = galleryPage * IMAGES_PER_PAGE;
    return randomizedImages.slice(start, start + IMAGES_PER_PAGE);
  }, [galleryPage, randomizedImages]);

  // Lightbox navigation handlers
  const openLightbox = useCallback((index) => {
    resetZoom();
    setLightboxIndex(index);
    setSelectedImage(randomizedImages[index]);
  }, [randomizedImages, resetZoom]);

  const closeLightbox = useCallback(() => {
    resetZoom();
    setSelectedImage(null);
  }, [resetZoom]);

  const nextImage = useCallback(() => {
    resetZoom();
    const newIndex = (lightboxIndex + 1) % randomizedImages.length;
    setLightboxIndex(newIndex);
    setSelectedImage(randomizedImages[newIndex]);
  }, [lightboxIndex, randomizedImages, resetZoom]);

  const prevImage = useCallback(() => {
    resetZoom();
    const newIndex = (lightboxIndex - 1 + randomizedImages.length) % randomizedImages.length;
    setLightboxIndex(newIndex);
    setSelectedImage(randomizedImages[newIndex]);
  }, [lightboxIndex, randomizedImages, resetZoom]);

  // Handle touch gestures for lightbox: pinch-zoom, pan when zoomed, swipe when not
  const handleTouchStart = useCallback((e) => {
    if (e.touches.length === 2) {
      pinchRef.current = { dist: getTouchDist(e.touches), scale: zoom.scale, pinching: true };
      return;
    }
    if (zoom.scale > 1) {
      // Pan candidate — becomes an actual pan only after real movement (so taps fall through)
      panRef.current = {
        startX: e.touches[0].clientX - zoom.x,
        startY: e.touches[0].clientY - zoom.y,
        rawX: e.touches[0].clientX,
        rawY: e.touches[0].clientY,
        panning: false,
        candidate: true
      };
      return;
    }
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = e.touches[0].clientX;
  }, [zoom]);

  const handleTouchMove = useCallback((e) => {
    if (pinchRef.current.pinching && e.touches.length === 2) {
      const newScale = Math.min(4, Math.max(1, pinchRef.current.scale * getTouchDist(e.touches) / pinchRef.current.dist));
      setZoom(z => (newScale === 1 ? { scale: 1, x: 0, y: 0 } : { ...z, scale: newScale }));
      return;
    }
    if (panRef.current.candidate && e.touches.length === 1) {
      const t = e.touches[0];
      if (!panRef.current.panning) {
        const moved = Math.hypot(t.clientX - panRef.current.rawX, t.clientY - panRef.current.rawY);
        if (moved < 8) return;
        panRef.current.panning = true;
      }
      setZoom(z => ({
        ...z,
        x: t.clientX - panRef.current.startX,
        y: t.clientY - panRef.current.startY
      }));
      return;
    }
    touchEndX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (pinchRef.current.pinching) {
      if (e.touches.length < 2) pinchRef.current.pinching = false;
      return;
    }
    const wasPanning = panRef.current.panning;
    panRef.current.panning = false;
    panRef.current.candidate = false;
    if (wasPanning) return;
    
    // Swipe navigation only when not zoomed
    if (zoom.scale === 1) {
      const swipeThreshold = 50;
      const diff = touchStartX.current - touchEndX.current;
      if (Math.abs(diff) > swipeThreshold) {
        if (diff > 0) {
          nextImage();
        } else {
          prevImage();
        }
        return;
      }
    }
    
    // Double-tap on the image toggles zoom (in and out)
    if (e.target && e.target.tagName === 'IMG') {
      const now = Date.now();
      if (now - lastTapRef.current < 300) {
        setZoom(z => (z.scale > 1 ? { scale: 1, x: 0, y: 0 } : { scale: 2.5, x: 0, y: 0 }));
        lastTapRef.current = 0;
        return;
      }
      lastTapRef.current = now;
    }
  }, [nextImage, prevImage, zoom.scale]);

  // Handle keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedImage) return;
      
      if (e.key === 'Escape') {
        closeLightbox();
      } else if (e.key === 'ArrowRight') {
        nextImage();
      } else if (e.key === 'ArrowLeft') {
        prevImage();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage, closeLightbox, nextImage, prevImage]);

  // Gallery pagination handlers
  const nextGalleryPage = useCallback(() => {
    setGalleryPage((prev) => (prev + 1) % totalGalleryPages);
  }, [totalGalleryPages]);

  const prevGalleryPage = useCallback(() => {
    setGalleryPage((prev) => (prev - 1 + totalGalleryPages) % totalGalleryPages);
  }, [totalGalleryPages]);

  const handleGalleryTouchStart = useCallback((e) => {
    galleryTouchStartX.current = e.touches[0].clientX;
    galleryTouchEndX.current = e.touches[0].clientX;
  }, []);

  const handleGalleryTouchMove = useCallback((e) => {
    galleryTouchEndX.current = e.touches[0].clientX;
  }, []);

  const handleGalleryTouchEnd = useCallback(() => {
    const swipeThreshold = 50;
    const diff = galleryTouchStartX.current - galleryTouchEndX.current;
    
    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        nextGalleryPage();
      } else {
        prevGalleryPage();
      }
    }
  }, [nextGalleryPage, prevGalleryPage]);

  const goToPage = useCallback((pageIndex) => {
    setGalleryPage(pageIndex);
  }, []);

  return (
    <section id="gallery" className="gallery-section" aria-labelledby="gallery-title">
      <h2 id="gallery-title" className="section-title">{t.galleryTitle}</h2>
      
      {/* Gallery with Pagination */}
      <div 
        className="gallery-slider-container"
        onTouchStart={handleGalleryTouchStart}
        onTouchMove={handleGalleryTouchMove}
        onTouchEnd={handleGalleryTouchEnd}
      >
        {/* Previous Page Arrow */}
        <button 
          className="gallery-nav-btn gallery-prev"
          onClick={prevGalleryPage}
          data-testid="gallery-prev-page"
          aria-label={language === 'cs' ? 'Předchozí strana' : 'Previous page'}
        >
          <ChevronLeft size={32} />
        </button>
        
        <div className="gallery-grid" role="grid" aria-label={t.galleryTitle}>
          {currentGalleryImages.map((img, index) => {
            const globalIndex = galleryPage * IMAGES_PER_PAGE + index;
            return (
              <div 
                key={`gallery-img-${globalIndex}-${img.split('/').pop()}`}
                className="gallery-item"
                onClick={() => openLightbox(globalIndex)}
                data-testid={`gallery-item-${globalIndex}`}
                role="button"
                tabIndex={0}
                aria-label={language === 'cs' ? `Otevřít fotografii ${globalIndex + 1} z galerie` : `Open gallery image ${globalIndex + 1}`}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openLightbox(globalIndex); }}
              >
                <img 
                  src={img} 
                  alt={language === 'cs' ? `UrbanCage multisportovní hřiště – fotografie ${globalIndex + 1}` : `UrbanCage multi-sport court - image ${globalIndex + 1}`}
                  loading="lazy"
                  width="400"
                  height="300"
                />
              </div>
            );
          })}
        </div>
        
        {/* Next Page Arrow */}
        <button 
          className="gallery-nav-btn gallery-next"
          onClick={nextGalleryPage}
          data-testid="gallery-next-page"
          aria-label={language === 'cs' ? 'Další strana' : 'Next page'}
        >
          <ChevronRight size={32} />
        </button>
      </div>
      
      {/* Page Indicator */}
      <div className="gallery-pagination" role="navigation" aria-label="Gallery pagination">
        <span className="gallery-page-indicator" aria-live="polite">
          {galleryPage + 1} / {totalGalleryPages}
        </span>
        <div className="gallery-dots" role="tablist">
          {Array.from({ length: totalGalleryPages }).map((_, i) => (
            <button
              key={`gallery-page-dot-${i}`}
              className={`gallery-dot ${i === galleryPage ? 'active' : ''}`}
              onClick={() => goToPage(i)}
              data-testid={`gallery-dot-${i}`}
              role="tab"
              aria-selected={i === galleryPage}
              aria-label={`Go to page ${i + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Lightbox with Navigation */}
      {selectedImage && (
        <div 
          className="lightbox" 
          onClick={closeLightbox}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          data-testid="lightbox-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={language === 'cs' ? 'Náhled fotografie' : 'Image preview'}
        >
          <button 
            className="lightbox-close" 
            onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
            data-testid="lightbox-close"
            aria-label={language === 'cs' ? 'Zavřít' : 'Close'}
          >
            <X size={32} />
          </button>
          
          {/* Previous Arrow */}
          <button 
            className="lightbox-nav lightbox-prev"
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            data-testid="lightbox-prev"
            aria-label={language === 'cs' ? 'Předchozí fotografie' : 'Previous image'}
          >
            <ChevronLeft size={40} />
          </button>
          
          <img 
            src={selectedImage} 
            alt={`Gallery full view ${lightboxIndex + 1}`}
            onClick={(e) => e.stopPropagation()}
            data-testid="lightbox-image"
            style={{
              transform: `translate(${zoom.x}px, ${zoom.y}px) scale(${zoom.scale})`,
              transition: pinchRef.current.pinching || panRef.current.panning ? 'none' : 'transform 0.2s ease-out'
            }}
          />
          
          {/* Next Arrow */}
          <button 
            className="lightbox-nav lightbox-next"
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            data-testid="lightbox-next"
            aria-label={language === 'cs' ? 'Další fotografie' : 'Next image'}
          >
            <ChevronRight size={40} />
          </button>
          
          {/* Image counter */}
          <div className="lightbox-counter" onClick={(e) => e.stopPropagation()} aria-live="polite">
            {lightboxIndex + 1} / {randomizedImages.length}
          </div>
        </div>
      )}
    </section>
  );
};

export default React.memo(Gallery);
